import { useState } from "react";
import { useForm } from "react-hook-form";
import styles from "../App.module.css";
import { MarketingFooter, MarketingNav } from "../Components/MarketingSite";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import API from "../API";

// Kept in sync by hand with credits/topupPacks.js in creataaiAPI - display
// copy only, the backend never trusts anything the client sends about price.
const PACKS = [
  { id: "small", name: "Small", credits: 100, price: "$2.99", perCredit: "$0.0299/credit" },
  { id: "medium", name: "Medium", credits: 300, price: "$6.99", perCredit: "$0.0233/credit" },
  { id: "large", name: "Large", credits: 800, price: "$14.99", perCredit: "$0.0187/credit" },
];

// Custom amounts are always priced at the Small pack's rate - see
// customPackFor()'s comment in creataaiAPI/credits/topupPacks.js for why.
const CUSTOM_MIN_AMOUNT = 2.99;
const CUSTOM_MAX_AMOUNT = 49.99;
const CUSTOM_CREDITS_PER_DOLLAR = PACKS[0].credits / 2.99;

const ACCENT = "#8df6d7";

const Topup = () => {
  const [selectedPack, setSelectedPack] = useState("medium");
  const [customAmount, setCustomAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const username = watch("username", "");
  const confirmUsername = watch("confirmUsername", "");

  const isCustomSelected = selectedPack === "custom";
  const customAmountNumber = parseFloat(customAmount);
  const customAmountValid =
    !Number.isNaN(customAmountNumber) &&
    customAmountNumber >= CUSTOM_MIN_AMOUNT &&
    customAmountNumber <= CUSTOM_MAX_AMOUNT;
  const estimatedCustomCredits = customAmountValid
    ? Math.round(customAmountNumber * CUSTOM_CREDITS_PER_DOLLAR)
    : null;

  const onSubmit = async (data) => {
    if (data.username.trim() !== data.confirmUsername.trim()) {
      setErrorMessage("Username and confirmation don't match - double-check for typos.");
      return;
    }
    if (isCustomSelected && !customAmountValid) {
      setErrorMessage(
        `Enter a custom amount between $${CUSTOM_MIN_AMOUNT.toFixed(2)} and $${CUSTOM_MAX_AMOUNT.toFixed(2)}.`
      );
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      const result = await API.createTopupSession({
        pack: selectedPack,
        amount: isCustomSelected ? customAmountNumber : undefined,
        username: data.username.trim(),
        email: data.email || undefined,
      });
      window.location.href = result.url;
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong starting checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.landingPage}>
      <MarketingNav currentPage="topup" />
      <Container className={styles.topupContent} maxWidth="md">
        <Typography variant="h3" gutterBottom>
          Buy Creata Ai Credits
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ marginBottom: 5 }}>
          Top up your account balance - credits never expire.
        </Typography>

        <Grid container spacing={3} sx={{ marginBottom: 4 }}>
          {PACKS.map((pack) => {
            const selected = selectedPack === pack.id;
            return (
              <Grid item xs={12} sm={6} md={3} key={pack.id}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelectedPack(pack.id)}
                  aria-pressed={selected}
                  sx={{
                    cursor: "pointer",
                    color: "inherit",
                    font: "inherit",
                    width: "100%",
                    border: selected ? `2px solid ${ACCENT}` : "2px solid rgba(255,255,255,0.15)",
                    backgroundColor: selected ? "rgba(141,246,215,0.08)" : "transparent",
                    borderRadius: 2,
                    padding: 3,
                    textAlign: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="h5" gutterBottom>
                    {pack.name}
                  </Typography>
                  <Typography variant="h6" sx={{ color: ACCENT }} gutterBottom>
                    {pack.credits} credits
                  </Typography>
                  <Typography variant="body1">{pack.price}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pack.perCredit}
                  </Typography>
                </Box>
              </Grid>
            );
          })}

          <Grid item xs={12} sm={6} md={3}>
            {/* A plain div, not component="button" - it contains a TextField,
                and a <button> cannot legally contain a nested <input>. */}
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setSelectedPack("custom")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedPack("custom");
                }
              }}
              aria-pressed={isCustomSelected}
              sx={{
                cursor: "pointer",
                color: "inherit",
                font: "inherit",
                width: "100%",
                border: isCustomSelected
                  ? `2px solid ${ACCENT}`
                  : "2px solid rgba(255,255,255,0.15)",
                backgroundColor: isCustomSelected ? "rgba(141,246,215,0.08)" : "transparent",
                borderRadius: 2,
                padding: 3,
                textAlign: "center",
                height: "100%",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Custom
              </Typography>
              <TextField
                size="small"
                placeholder="Amount"
                value={customAmount}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setSelectedPack("custom");
                }}
                onFocus={() => setSelectedPack("custom")}
                InputProps={{
                  startAdornment: <Typography sx={{ marginRight: 0.5 }}>$</Typography>,
                }}
                inputProps={{
                  inputMode: "decimal",
                  "aria-label": "Custom amount in dollars",
                }}
                sx={{ marginBottom: 1, maxWidth: 140 }}
              />
              <Typography variant="body2" color="text.secondary">
                ${CUSTOM_MIN_AMOUNT.toFixed(2)} - ${CUSTOM_MAX_AMOUNT.toFixed(2)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: ACCENT, minHeight: "1.5em", marginTop: 0.5 }}
              >
                {isCustomSelected && customAmountValid
                  ? `~${estimatedCustomCredits} credits`
                  : " "}
              </Typography>
              {isCustomSelected && customAmount && !customAmountValid && (
                <Typography variant="body2" color="error">
                  Must be ${CUSTOM_MIN_AMOUNT.toFixed(2)}-${CUSTOM_MAX_AMOUNT.toFixed(2)}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ maxWidth: 480 }}>
          <Typography variant="body1" gutterBottom>
            Your Creata Ai username (required):
          </Typography>
          <TextField
            fullWidth
            placeholder="Find this on the Profile screen in the app"
            sx={{ marginBottom: 2 }}
            {...register("username", { required: true })}
          />

          <Typography variant="body1" gutterBottom>
            Confirm username:
          </Typography>
          <TextField
            fullWidth
            placeholder="Type it again to avoid typos"
            sx={{ marginBottom: 1 }}
            error={
              !!confirmUsername && !!username && confirmUsername.trim() !== username.trim()
            }
            {...register("confirmUsername", { required: true })}
          />
          <Typography variant="body2" color="warning.main" sx={{ marginBottom: 3 }}>
            Credits are added to whichever username you enter - open Profile in the Creata Ai
            app to find yours, and double-check it before continuing.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Email (optional, for your receipt):
          </Typography>
          <TextField
            fullWidth
            type="email"
            sx={{ marginBottom: 3 }}
            {...register("email")}
          />

          {(errors.username || errors.confirmUsername) && (
            <Typography variant="body2" color="error" sx={{ marginBottom: 2 }}>
              Please fill in your username and confirm it.
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {submitting ? "Starting checkout..." : "Checkout"}
          </Button>
        </Box>
      </Container>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage("")}
      >
        <Alert onClose={() => setErrorMessage("")} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <MarketingFooter />
    </main>
  );
};

export default Topup;
